"use client"

import * as React from "react"
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area"

import { cn } from "@/lib/utils"

const SHADOW_SIZE = 60;

interface IScrollAreaProps
  extends React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> {
  /** Set to false to disable scroll-edge shadow indicators. Default true. */
  scrollShadow?: boolean;
  /** Shadow size in pixels. Default 40. */
  shadowSize?: number;
}

function useScrollShadows(
  viewportRef: React.RefObject<HTMLDivElement | null>,
  enabled: boolean,
): { top: boolean; bottom: boolean } {
  const [shadows, setShadows] = React.useState({ top: false, bottom: false });

  React.useEffect(() => {
    const el = viewportRef.current;
    if (!el || !enabled) return;

    const update = (): void => {
      const t = 4;
      setShadows({
        top: el.scrollTop > t,
        bottom: el.scrollHeight - el.scrollTop - el.clientHeight > t,
      });
    };

    update();

    el.addEventListener("scroll", update, { passive: true });

    const ro = new ResizeObserver(update);
    ro.observe(el);

    if (el.firstElementChild) {
      ro.observe(el.firstElementChild);
    }

    return (): void => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [viewportRef, enabled]);

  return shadows;
}

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  IScrollAreaProps
>(({ className, children, scrollShadow = true, shadowSize = SHADOW_SIZE, ...props }, ref) => {
  const viewportRef = React.useRef<HTMLDivElement>(null);
  const shadows = useScrollShadows(viewportRef, scrollShadow);

  return (
    <ScrollAreaPrimitive.Root
      ref={ref}
      className={cn("relative overflow-hidden", className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport ref={viewportRef} className="h-full w-full rounded-[inherit]">
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar />
      <ScrollAreaPrimitive.Corner />

      {scrollShadow && shadows.top && (
        <div
          className="pointer-events-none absolute inset-x-0 top-0 z-10 transition-opacity duration-150"
          style={{
            height: shadowSize,
            background: "linear-gradient(white, transparent)",
          }}
        />
      )}
      {scrollShadow && shadows.bottom && (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-10 transition-opacity duration-150"
          style={{
            height: shadowSize,
            background: "linear-gradient(transparent, white)",
          }}
        />
      )}
    </ScrollAreaPrimitive.Root>
  );
})
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = "vertical", ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      "flex touch-none select-none transition-colors",
      orientation === "vertical" &&
        "h-full w-2.5 border-l border-l-transparent p-px",
      orientation === "horizontal" &&
        "h-2.5 flex-col border-t border-t-transparent p-px",
      className
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-border" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
))
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName

/**
 * Standalone shadow overlays for native scrollable containers that can't use
 * Radix ScrollArea (e.g. responsive `lg:overflow-y-auto` patterns). Place as a
 * sibling of the scrollable element inside a `relative` parent.
 */
function ScrollShadows({
  scrollRef,
  size = SHADOW_SIZE,
  top = true,
  bottom = true,
}: {
  scrollRef: React.RefObject<HTMLDivElement | null>;
  size?: number;
  /** Show the top shadow. Default true. */
  top?: boolean;
  /** Show the bottom shadow. Default true. */
  bottom?: boolean;
}): React.ReactElement | null {
  const shadows = useScrollShadows(scrollRef, true);

  const showTop = top && shadows.top;
  const showBottom = bottom && shadows.bottom;

  if (!showTop && !showBottom) return null;

  return (
    <>
      {showTop && (
        <div
          className="pointer-events-none absolute inset-x-0 top-0 z-10 transition-opacity duration-150"
          style={{
            height: size,
            background: "linear-gradient(white, transparent)",
          }}
        />
      )}
      {showBottom && (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-10 transition-opacity duration-150"
          style={{
            height: size,
            background: "linear-gradient(transparent, white)",
          }}
        />
      )}
    </>
  );
}

export { ScrollArea, ScrollBar, ScrollShadows, useScrollShadows }
